import { app } from '@microsoft/teams-js';
import { isTeamsEmbeddedOnLoad, isTeamsHost } from './teamsHost';
import { hasTeamsSession } from './teamsSession';
import { readRawLinkClick, readTeamsLinkAttempt } from './teamsLinkDebug';
import { getTeamsHostClientType, isTeamsSdkReady, shouldOpenInTeamsHost } from './teamsSdk';

export type TeamsDebugSnapshot = {
  pageUrl: string;
  pathname: string;
  search: string;
  hash: string;
  teamsQueryParam: string | null;
  hasTeamsQueryParam: boolean;
  inIframe: boolean;
  embeddedOnLoad: boolean;
  teamsSessionFlag: boolean;
  teamsAuthInProgress: boolean;
  sdkInitialized: boolean;
  sdkReady: boolean;
  shouldOpenInTeamsHost: boolean;
  hostClientType: string | null;
  documentReferrer: string;
  userAgent: string;
  contextError: string | null;
  contextHostName: string | null;
  contextFrameContext: string | null;
  contextTheme: string | null;
  lastLinkAttempt: string | null;
  lastRawClick: string | null;
};

export function collectTeamsDebugSnapshot(): Omit<
  TeamsDebugSnapshot,
  'contextError' | 'contextHostName' | 'contextFrameContext' | 'contextTheme'
> {
  const params = new URLSearchParams(window.location.search);
  const teamsParam = params.get('teams');

  return {
    pageUrl: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search || '(empty)',
    hash: window.location.hash || '(empty)',
    teamsQueryParam: teamsParam,
    hasTeamsQueryParam: teamsParam === 'true',
    inIframe: isTeamsHost(),
    embeddedOnLoad: isTeamsEmbeddedOnLoad(),
    teamsSessionFlag: hasTeamsSession(),
    teamsAuthInProgress: Boolean(sessionStorage.getItem('cw-ai-teams-auth-in-progress')),
    sdkInitialized: app.isInitialized(),
    sdkReady: isTeamsSdkReady(),
    shouldOpenInTeamsHost: shouldOpenInTeamsHost(),
    hostClientType: getTeamsHostClientType() ?? null,
    documentReferrer: document.referrer || '(empty)',
    userAgent: navigator.userAgent,
    lastLinkAttempt: (() => {
      const attempt = readTeamsLinkAttempt();
      if (!attempt) return '(none)';
      return `${attempt.at} | ${attempt.method} | ${attempt.url}${attempt.error ? ` | ${attempt.error}` : ''}`;
    })(),
    lastRawClick: (() => {
      const raw = readRawLinkClick();
      if (!raw) return '(none)';
      return `${raw.at} | ${raw.phase} | ${raw.tag} | ${raw.rawHref}`;
    })(),
  };
}

export async function enrichTeamsDebugSnapshot(
  base: ReturnType<typeof collectTeamsDebugSnapshot>,
): Promise<TeamsDebugSnapshot> {
  if (!app.isInitialized()) {
    return {
      ...base,
      contextError: 'Teams SDK not initialized',
      contextHostName: null,
      contextFrameContext: null,
      contextTheme: null,
    };
  }

  try {
    const context = await app.getContext();
    return {
      ...base,
      contextError: null,
      contextHostName: context.app?.host?.name ?? null,
      contextFrameContext: context.page?.frameContext ?? null,
      contextTheme: context.app?.theme ?? null,
    };
  } catch (error) {
    return {
      ...base,
      contextError: error instanceof Error ? error.message : String(error),
      contextHostName: null,
      contextFrameContext: null,
      contextTheme: null,
    };
  }
}

export function formatTeamsDebugSnapshot(snapshot: TeamsDebugSnapshot): string {
  return [
    `pageUrl: ${snapshot.pageUrl}`,
    `pathname: ${snapshot.pathname}`,
    `search: ${snapshot.search}`,
    `hash: ${snapshot.hash}`,
    `teams query param: ${snapshot.teamsQueryParam ?? '(missing)'}`,
    `hasTeamsQueryParam (teams=true): ${snapshot.hasTeamsQueryParam}`,
    `inIframe (self !== top): ${snapshot.inIframe}`,
    `embeddedOnLoad: ${snapshot.embeddedOnLoad}`,
    `teamsSessionFlag (sessionStorage): ${snapshot.teamsSessionFlag}`,
    `teamsAuthInProgress: ${snapshot.teamsAuthInProgress}`,
    `sdkInitialized: ${snapshot.sdkInitialized}`,
    `sdkReady: ${snapshot.sdkReady}`,
    `shouldOpenInTeamsHost: ${snapshot.shouldOpenInTeamsHost}`,
    `hostClientType: ${snapshot.hostClientType ?? '(unknown)'}`,
    `contextHostName: ${snapshot.contextHostName ?? '(n/a)'}`,
    `contextFrameContext: ${snapshot.contextFrameContext ?? '(n/a)'}`,
    `contextTheme: ${snapshot.contextTheme ?? '(n/a)'}`,
    `contextError: ${snapshot.contextError ?? '(none)'}`,
    `lastLinkAttempt: ${snapshot.lastLinkAttempt ?? '(none)'}`,
    `lastRawClick: ${snapshot.lastRawClick ?? '(none)'}`,
    `document.referrer: ${snapshot.documentReferrer}`,
    `userAgent: ${snapshot.userAgent}`,
  ].join('\n');
}
